import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { WorkBook } from 'xlsx';
import type { ColDef } from 'ag-grid-community';
import AgGridWrapper from '../../../blocks/tables/AgGridWrapper';
import { WidgetLoadingState } from '../../../design';

interface ExcelInlineViewerProps {
  fileUrl: string;
  fileName?: string;
}

type GridRow = Record<string, string>;

// Sheets we'd rather land on than the instructions/config tabs, if present.
const PREFERRED_DEFAULT = ['Model', 'Valuation', 'Guidance'];

/**
 * Parses an .xlsx (SheetJS, dynamically imported so it stays out of the main
 * bundle) and renders each sheet as a spreadsheet-style grid with sheet tabs.
 * Read-only — shows the workbook's cached cell values, not live formulas.
 */
const ExcelInlineViewer: React.FC<ExcelInlineViewerProps> = ({ fileUrl, fileName }) => {
  const wbRef = useRef<WorkBook | null>(null);
  const xlsxRef = useRef<typeof import('xlsx') | null>(null);
  const [sheets, setSheets] = useState<string[]>([]);
  const [activeSheet, setActiveSheet] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch + parse the workbook once per file.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const [XLSX, res] = await Promise.all([import('xlsx'), fetch(fileUrl)]);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const buf = await res.arrayBuffer();
        const wb = XLSX.read(buf, { type: 'array' });
        if (cancelled) return;
        xlsxRef.current = XLSX;
        wbRef.current = wb;
        const names = wb.SheetNames;
        setSheets(names);
        setActiveSheet(PREFERRED_DEFAULT.find((n) => names.includes(n)) || names[0] || '');
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'parse failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fileUrl]);

  // Build the grid for the active sheet from the already-parsed workbook.
  const { columnDefs, rowData } = useMemo<{ columnDefs: ColDef<GridRow>[]; rowData: GridRow[] }>(() => {
    const XLSX = xlsxRef.current;
    const wb = wbRef.current;
    const ws = wb && activeSheet ? wb.Sheets[activeSheet] : undefined;
    if (!XLSX || !ws) return { columnDefs: [], rowData: [] };

    const rawAoa = XLSX.utils.sheet_to_json<unknown[]>(ws, {
      header: 1,
      raw: false, // formatted text, matching Excel's display
      defval: '',
      blankrows: false,
    });

    // The raw InSync model is dense with label-only / placeholder rows (empty
    // Guide-Low/High/Mid, sub-headers, sparse spacers) that just add clutter.
    // Keep only rows that carry actual content (>= 2 non-empty cells).
    const aoa = rawAoa.filter((r) => {
      let nonEmpty = 0;
      for (const c of r) if (c !== '' && c != null) nonEmpty++;
      return nonEmpty >= 2;
    });

    // Trim trailing empty columns so we don't render dozens of blank columns.
    let maxCols = 0;
    for (const r of aoa) {
      for (let c = r.length - 1; c >= 0; c--) {
        if (r[c] !== '' && r[c] != null) {
          if (c + 1 > maxCols) maxCols = c + 1;
          break;
        }
      }
    }

    const cols: ColDef<GridRow>[] = [];
    for (let c = 0; c < maxCols; c++) {
      // Column E holds the InSync line-item names — give it room so they read in
      // full instead of truncating ("Research…", "Total Oper…").
      const isNameCol = c === 4;
      cols.push({
        headerName: XLSX.utils.encode_col(c),
        field: `c${c}`,
        width: isNameCol ? 600 : 120,
        minWidth: 56,
      });
    }
    const rows: GridRow[] = aoa.map((r) => {
      const obj: GridRow = {};
      for (let c = 0; c < maxCols; c++) obj[`c${c}`] = (r[c] ?? '') as string;
      return obj;
    });
    return { columnDefs: cols, rowData: rows };
  }, [activeSheet]);

  const defaultColDef = useMemo<ColDef>(() => ({ sortable: false, filter: false, resizable: true }), []);

  if (loading) return <WidgetLoadingState />;
  if (error) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-6 text-center bg-surface">
        <p className="text-sm text-danger-600 mb-2">Couldn&apos;t display this workbook ({error}).</p>
        <a href={fileUrl} target="_blank" rel="noreferrer" className="text-sm text-success-600 underline">
          Open in Excel instead
        </a>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden bg-surface">
      <div className="flex items-center gap-1 px-2 py-1.5 border-b border-neutral-200 overflow-x-auto shrink-0">
        {sheets.map((name) => (
          <button
            key={name}
            onClick={() => setActiveSheet(name)}
            className={`px-2.5 py-1 text-xs rounded whitespace-nowrap transition-colors ${
              name === activeSheet
                ? 'bg-success-600 text-white font-medium'
                : 'text-neutral-600 hover:bg-neutral-100'
            }`}
          >
            {name}
          </button>
        ))}
        <a
          href={fileUrl}
          target="_blank"
          rel="noreferrer"
          title={fileName ? `Open ${fileName} in Excel` : 'Open in Excel'}
          className="ml-auto pl-3 pr-1 text-xs text-neutral-500 hover:text-success-600 whitespace-nowrap"
        >
          Open in Excel ↗
        </a>
      </div>

      {/* Intermediate flex-1 + overflow-hidden wrapper gives ag-grid a definite
          height to fill (same pattern as ConsensusTable); putting flex-1 directly
          on the grid container makes ag-grid fall back to a short default height. */}
      <div className="flex-1 overflow-hidden">
        <AgGridWrapper<GridRow>
          containerClassName="ag-theme-alpine h-full w-full"
          columnDefs={columnDefs}
          rowData={rowData}
          defaultColDef={defaultColDef}
          headerHeight={28}
          rowHeight={24}
        />
      </div>
    </div>
  );
};

export default ExcelInlineViewer;
